import React, { useState, useEffect } from "react";
import { IoIosArrowBack, IoIosArrowForward, IoIosClose } from "react-icons/io";
import { FiTrash2 } from "react-icons/fi";
import { RiGalleryView2 } from "react-icons/ri";
import { ImSpinner2 } from "react-icons/im";
import ReactPlayer from "react-player";
import { MediaItem } from "../../../../types/item";
import { MediaType } from "../../../../types/enums/item";
import { getImage } from "../../../../db";

interface MediaViewerProps {
  media: MediaItem[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  onViewGallery: () => void;
  onDelete?: () => void;
  className?: string;
  showGalleryButton?: boolean;
}

const MediaViewer: React.FC<MediaViewerProps> = ({
  media,
  currentIndex,
  onClose,
  onNext,
  onPrev,
  onViewGallery,
  onDelete,
  className = "",
  showGalleryButton = true,
}) => {
  const [mediaUrl, setMediaUrl] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const currentMedia = media[currentIndex];

  // Load media URL from cloud or IndexedDB
  useEffect(() => {
    const loadUrl = async () => {
      if (!currentMedia) {
        setError(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(false);

      try {
        // If we have a cloud URL, use it
        if (currentMedia.url) {
          setMediaUrl(currentMedia.url);
        } else if (currentMedia.uuid) {
          // Get blob and create object URL
          const imageResult = await getImage(currentMedia.uuid);
          if (imageResult?.blob) {
            setMediaUrl(URL.createObjectURL(imageResult.blob));
          } else {
            setError(true);
          }
        } else {
          setError(true);
        }
      } catch (e) {
        console.warn(
          `Failed to resolve media for UUID ${currentMedia.uuid}`,
          e
        );
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadUrl();
  }, [currentMedia?.url, currentMedia?.uuid, currentIndex]);

  const handleMediaError = () => {
    setError(true);
    setLoading(false);
  };

  if (!media || media.length === 0) return null;

  return (
    <div
      className={`fixed inset-0 top-0 left-0 right-0 bottom-0 bg-black z-[100] ${className}`}
    >
      {/* Media Container */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center">
            <ImSpinner2 className="animate-spin text-white text-4xl" />
          </div>
        ) : error || !mediaUrl ? (
          <div className="flex flex-col items-center justify-center p-8 max-w-md">
            <svg
              className="w-32 h-32 text-gray-500 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="text-xl text-white text-center font-medium mb-2">
              {currentMedia?.type_ID === MediaType.Video
                ? "Video unavailable"
                : "Image unavailable"}
            </p>
            <p className="text-base text-gray-400 text-center">
              Offline or unreachable
            </p>
          </div>
        ) : currentMedia?.type_ID === MediaType.Video ? (
          <div className="flex items-center justify-center">
            {/* @ts-ignore - ReactPlayer types */}
            <ReactPlayer
              src={mediaUrl}
              controls={true}
              width="100%"
              height="100%"
              playing={false}
              playsInline
              onError={handleMediaError}
            />
          </div>
        ) : (
          <img
            src={mediaUrl}
            alt={`Media ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain"
            onError={handleMediaError}
          />
        )}
      </div>

      {/* Top Bar with Counter and Close Button */}
      <div className="absolute top-3 left-0 right-0 flex justify-between items-center flex-grow-0 h-25 z-30">
        {/* Media Counter */}
        <div className="bg-black bg-opacity-50 text-white px-3 py-2 rounded-full text-sm mx-auto">
          {currentIndex + 1} of {media.length}
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="p-2 pt-1 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
        >
          <IoIosClose size={32} className="p-0" />
        </button>
      </div>

      {/* Bottom Controls - Navigation and Actions */}
      <div className="absolute bottom-5 left-0 right-0 flex flex-col items-center gap-4 z-30">
        {/* Navigation Arrows */}
        {media.length > 1 && (
          <div className="flex justify-center gap-4 items-center">
            <button
              onClick={onPrev}
              className="p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
            >
              <IoIosArrowBack size={24} />
            </button>
            <button
              onClick={onNext}
              className="p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
            >
              <IoIosArrowForward size={24} />
            </button>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex justify-center gap-2 items-center">
        {showGalleryButton && (
          <button
            onClick={onViewGallery}
            className="py-2 px-4 bg-black bg-opacity-50 hover:bg-gray-800 text-white rounded-lg hover:bg-opacity-70 transition-colors flex items-center gap-2"
          >
            <RiGalleryView2 size={24} />
            <span className="text-sm">Gallery</span>
          </button>
        )}
          <button 
            onClick={onDelete}
            disabled={!onDelete}
            className="p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-gray-800 hover:bg-opacity-70 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiTrash2 size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaViewer;
